'use client';

import { PropsWithChildren } from 'react';
import { alpha, Box, Fade } from '@mui/material';
import LoadingMessage from './LoadingMessage';

type Props = {
  active: boolean;
  messages?: string[];
};

/**
 * Blurs the wrapped form behind a glass surface and shows the configured
 * loading messages while the submission is in flight. Renders the children
 * untouched when no messages are configured.
 */
const FormLoadingOverlay = ({
  active,
  messages,
  children,
}: PropsWithChildren<Props>) => {
  if (!messages?.length) return children;

  return (
    <Box sx={{ position: 'relative' }}>
      <Box inert={active || undefined}>{children}</Box>
      <Fade in={active} timeout={300} unmountOnExit>
        <Box
          sx={{
            position: 'absolute',
            inset: '-8px',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: { xs: 4, md: 6 },
            borderRadius: 'inherit',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            backgroundColor: (theme) =>
              alpha(theme.palette.background.paper, 0.65),
          }}
        >
          <LoadingMessage messages={messages} active={active} />
        </Box>
      </Fade>
    </Box>
  );
};

export default FormLoadingOverlay;
